/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the function used to create the ScreenFailures reducer.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * External Imports
 */
import { IRequestState, RequestState } from '@ngt/request-utilities';
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';
import { createLogic } from 'redux-logic';
import { ReducerRegistry } from '@ngt/reducer-registry-logics';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { JsonServiceClient } from '@servicestack/client';
import { Reducer } from 'redux';

/*
 * Internal Imports
 */
import * as Dtos from '../../../api/dtos';

/*
 * Interfaces
 */

export interface IScreenFailuresState {
    screenFailures: Dtos.ScreenFailure[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
}

export interface IScreenFailuresStore {
    screenFailures: IScreenFailuresState;
}

/*
 * Reducer Class
 */
export class ScreenFailuresReducerClass extends ImmerReducer<IScreenFailuresState> {
    public load() {
        this.draftState.loadState = { state: RequestState.Pending };
    }

    public loadSuccess(screenFailures?: Dtos.ScreenFailure[] | null) {
        this.draftState.screenFailures = screenFailures ?? null;
        this.draftState.loadState = { state: RequestState.Success };
    }

    public loadFailure(responseStatus?: Dtos.ResponseStatus) {
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus,
        };
    }

    public clear() {
        this.draftState = { ...initialScreenFailuresState };
    }
}

/*
 * Initial State
 */
export const initialScreenFailuresState: IScreenFailuresState = {
    screenFailures: null,
    loadState: { state: RequestState.None },
};

/*
 * Actions and Reducer
 */
export const screenFailuresActions = createActionCreators(ScreenFailuresReducerClass);
export const screenFailuresReducer = createReducerFunction(ScreenFailuresReducerClass, initialScreenFailuresState);

/*
 * API
 */
const createScreenFailuresApi = (client: JsonServiceClient) => ({
    load: () => client.get(new Dtos.GetScreenFailures()),
});

/*
 * Logic
 */
const createScreenFailuresLogic = (api: ReturnType<typeof createScreenFailuresApi>) => {
    const logic = {
        load: createLogic<IScreenFailuresStore, {}, undefined, string, ReturnType<typeof screenFailuresActions.load>>({
            type: screenFailuresActions.load.type,
            process: async ({ action: _action }, dispatch, done) => {
                try {
                    const response = await api.load();

                    dispatch(screenFailuresActions.loadSuccess(response.screenFailures));
                } catch (error: any) {
                    dispatch(screenFailuresActions.loadFailure(error ? error?.responseStatus : undefined));
                }
                done();
            },
        }),
    };

    return [logic.load];
};

/*
 * Selector
 */
export const useScreenFailuresSelector: TypedUseSelectorHook<IScreenFailuresStore> = useSelector;

export const screenFailuresSelectors = {
    selectScreenFailures: (state: IScreenFailuresStore) => state.screenFailures?.screenFailures ?? initialScreenFailuresState.screenFailures,
    selectLoadState: (state: IScreenFailuresStore) => state.screenFailures?.loadState ?? initialScreenFailuresState.loadState,
};

/*
 * Registration
 */
const registerScreenFailuresReducer = (client: JsonServiceClient, reducerRegistry: ReducerRegistry) => {
    const api = createScreenFailuresApi(client);
    const logic = createScreenFailuresLogic(api);
    reducerRegistry.register('screenFailures', screenFailuresReducer as Reducer, logic as any);
};

export default registerScreenFailuresReducer;
